/**
 * Hero Logo — fills glyph containers to match the width of "Brussels".
 * Measures the reference word, calculates remaining space for each line,
 * then injects as many glyphs as fit.
 *
 * Shuffle (with header logo, same interval): fade glyphs → measure → flip
 * By/Night alignment (word + fill slide on translate) → repopulate → fade in.
 */

import {
  LOGO_ANIM_FADE_MS,
  LOGO_ANIM_SLIDE_MS,
} from "./logo-animation-timings";

const GLYPH_SVG = `<svg class="c-site-logo__glyph" width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M53 27C53 38.0457 61.9543 47 73 47H100V53H73C61.9543 53 53 61.9543 53 73V100H47V73C47 61.9543 38.0457 53 27 53H0V47H27C38.0457 47 47 38.0457 47 27V0H53V27Z" fill="currentColor"/></svg>`;
let heroLogoResizeHandler = null;

function measureGlyph(container) {
  container.insertAdjacentHTML("beforeend", GLYPH_SVG);
  const glyph = container.lastElementChild;
  const w = glyph.getBoundingClientRect().width;
  glyph.remove();
  return w;
}

/**
 * Pick new By/Night sides so at least one line changes (never identical to prev).
 * Only update `data-align` on lines that flip so unchanged lines skip translateX.
 */
function flipFillLinesAlign(logo) {
  const lines = Array.from(logo.querySelectorAll(".c-site-logo__line--fill"));
  if (lines.length < 2) return;

  const prev = lines.map((l) =>
    l.getAttribute("data-align") === "right" ? "right" : "left"
  );

  let next = [
    Math.random() < 0.5 ? "left" : "right",
    Math.random() < 0.5 ? "left" : "right",
  ];

  if (next[0] === prev[0] && next[1] === prev[1]) {
    const toggleIdx = Math.random() < 0.5 ? 0 : 1;
    next[toggleIdx] = prev[toggleIdx] === "left" ? "right" : "left";
  }

  lines.forEach((line, i) => {
    if (next[i] !== prev[i]) {
      line.setAttribute("data-align", next[i]);
    }
  });
}

/**
 * Update slide distance CSS vars without animating transform (changing --hero-*
 * while transition is on retriggers translate interpolation).
 */
function setHeroLineSlideVarsInstant(line) {
  const word = line.querySelector(".c-site-logo__word");
  const fill = line.querySelector("[data-hero-fill]");
  if (!word || !fill) return;
  line.classList.add("c-site-logo__line--fill--instant-geom");
  const gap = parseFloat(getComputedStyle(line).columnGap) || 0;
  line.style.setProperty("--hero-gap-px", `${gap}px`);
  line.style.setProperty("--hero-fill-px", `${fill.offsetWidth}px`);
  line.style.setProperty("--hero-word-px", `${word.offsetWidth}px`);
  void line.offsetHeight;
  line.classList.remove("c-site-logo__line--fill--instant-geom");
}

function populate(logo) {
  const measure = logo.querySelector("[data-hero-measure]");
  const fills = logo.querySelectorAll("[data-hero-fill]");
  if (!measure || !fills.length) return;

  const brusselsWidth = measure.getBoundingClientRect().width;
  if (brusselsWidth <= 0) return;

  fills.forEach((fill) => {
    fill.innerHTML = "";

    const line = fill.closest(".c-site-logo__line");
    const word = line && line.querySelector(".c-site-logo__word");
    if (!line || !word) return;

    const wordWidth = word.getBoundingClientRect().width;
    const lineGap = parseFloat(getComputedStyle(line).columnGap) || 0;
    const fillWidth = brusselsWidth - wordWidth - lineGap;

    if (fillWidth <= 0) return;

    fill.style.flex = "0 0 " + fillWidth + "px";

    const glyphWidth = measureGlyph(fill);
    const fillGap = parseFloat(getComputedStyle(fill).columnGap) || 0;
    const maxFitCount = Math.floor((fillWidth + fillGap) / (glyphWidth + fillGap));
    const preferredCount = fill.dataset.line === "by" ? 5 : 1;

    let count = Math.max(0, preferredCount);
    for (let i = 0; i < count; i++) {
      fill.insertAdjacentHTML("beforeend", GLYPH_SVG);
    }

    while (count > 0 && fill.scrollWidth > fill.clientWidth + 1) {
      const last = fill.lastElementChild;
      if (!last) break;
      last.remove();
      count -= 1;
    }

    if (count === 0 && maxFitCount > 0) {
      fill.insertAdjacentHTML("beforeend", GLYPH_SVG);
    }

    setHeroLineSlideVarsInstant(line);
  });
}

/**
 * Called on the same interval as header logo shuffle (see logo-shuffle.js).
 */
export function shuffleHeroStackedLogo() {
  const logo = document.querySelector("[data-hero-logo]");
  if (!logo || logo.dataset.heroShuffling === "1") return;

  const fills = Array.from(logo.querySelectorAll("[data-hero-fill]"));
  if (fills.length === 0) return;

  logo.dataset.heroShuffling = "1";
  fills.forEach((f) => f.classList.add("is-fading"));

  setTimeout(() => {
    if (!logo.isConnected) {
      delete logo.dataset.heroShuffling;
      return;
    }

    const fillLines = Array.from(logo.querySelectorAll(".c-site-logo__line--fill"));
    fillLines.forEach((line) => setHeroLineSlideVarsInstant(line));

    flipFillLinesAlign(logo);

    setTimeout(() => {
      if (!logo.isConnected) {
        delete logo.dataset.heroShuffling;
        return;
      }

      fillLines.forEach((line) => {
        if (line.isConnected) {
          line.classList.add("c-site-logo__line--fill--layout-only");
        }
      });

      fills.forEach((f) => {
        if (!f.isConnected) return;
        f.innerHTML = "";
        f.style.flex = "";
      });

      void logo.offsetWidth;
      populate(logo);

      requestAnimationFrame(() => {
        fillLines.forEach((line) => {
          if (!line.isConnected) return;
          line.classList.add("c-site-logo__line--fill--instant-geom");
          line.classList.remove("c-site-logo__line--fill--layout-only");
          void line.offsetHeight;
          line.classList.remove("c-site-logo__line--fill--instant-geom");
        });
      });

      setTimeout(() => {
        if (logo.isConnected) {
          logo.querySelectorAll("[data-hero-fill].is-fading").forEach((el) => {
            el.classList.remove("is-fading");
          });
        }
        delete logo.dataset.heroShuffling;
      }, LOGO_ANIM_SLIDE_MS);
    }, LOGO_ANIM_SLIDE_MS);
  }, LOGO_ANIM_FADE_MS);
}

export function initHeroLogo() {
  if (heroLogoResizeHandler) {
    window.removeEventListener("resize", heroLogoResizeHandler);
    heroLogoResizeHandler = null;
  }

  const logo = document.querySelector("[data-hero-logo]");
  if (!logo) return;

  function run() {
    requestAnimationFrame(() => populate(logo));
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(run);
  } else {
    run();
  }

  const scrollToSections = () => {
    const sections = document.querySelector(".c-home-sections");
    if (!(sections instanceof HTMLElement)) return;
    sections.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (logo.dataset.scrollBound !== "true") {
    logo.addEventListener("click", scrollToSections);
    logo.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        scrollToSections();
      }
    });
    logo.dataset.scrollBound = "true";
  }

  heroLogoResizeHandler = () => run();
  window.addEventListener("resize", heroLogoResizeHandler);
}
